import { AutoComplete, Form, Input, Modal } from "antd";
import React, { useContext, useEffect, useState } from "react";
import {
  getValueFromLabel,
  getLabelsAsValues,
  SelectOptionsType,
  createRegex,
} from "../../../utils/select-helpers";
import { getPools } from "../../../api/numbers/getPools";
import { postSipNumbersBulk } from "../../../api/numbers/postSipNumbersBulk";
import HeaderContext from "../../../components/layout/header/header-context/HeaderContext";
import NumericValidator from "../validators/NumericValidator";
import LengthValidator from "../validators/LengthValidator";

interface Props {
  isVisible: boolean;
  handleClose: (refreshData?: boolean) => void;
}

interface State {
  isModalVisible: boolean;
  pools?: SelectOptionsType[];
  submitError?: string;
  poolRegex: RegExp;
}

const AddNewNumbersFormModal: React.FC<Props> = ({
  isVisible,
  handleClose,
}) => {
  const [form] = Form.useForm();
  const { currentUserId } = useContext(HeaderContext);

  const [state, setState] = useState<State>({
    isModalVisible: false,
    pools: undefined,
    poolRegex: / /,
  });

  const { isModalVisible, pools, submitError, poolRegex } = state;

  const handleCancel = () => {
    form.resetFields();
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    handleClose();
  };

  const handleSubmit = () => {
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    form
      .validateFields()
      .then((formFields) => {
        console.log("onOk formFields: %o", formFields);
        const payload = {
          poolId: Number(getValueFromLabel(formFields.pool, pools)),
          phoneNumbers: formFields.telephoneNumbers
            .split(",")
            .map((number: string) => number.trim()),
          clientId: currentUserId,
        };
        console.log("postSipNumbersBulk() payload: %o", payload);
        postSipNumbersBulk(payload).then((postResponse) => {
          console.log("submitResponse:%o", postResponse);
          if (postResponse?.error) {
            setState((prevState) => ({
              ...prevState,
              submitError: postResponse.error,
            }));
          } else {
            form.resetFields();
            setState((prevState) => ({
              ...prevState,
              submitError: "",
            }));
            handleClose();
          }
        });
      })
      .catch((err) => {
        console.log("onOk err: %o", err);
      });
  };

  useEffect(() => {
    if (isVisible !== isModalVisible) {
      setState((prevState) => ({
        ...prevState,
        isModalVisible: isVisible,
        submitError: "",
      }));
    }

    if (!isVisible) return;

    if (!pools)
      getPools().then((response) => {
        if (response.data) {
          setState((prevState) => ({ ...prevState, pools: response.data }));
        }
      });

    // eslint-disable-next-line
  }, [isVisible, isModalVisible]);

  return (
    <Modal
      title="Add New Numbers"
      maskClosable={false}
      open={isModalVisible}
      onCancel={handleCancel}
      cancelText="Cancel"
      okText="Submit"
      onOk={handleSubmit}
      okButtonProps={{
        className: "bdo-red-button-modal",
      }}
      cancelButtonProps={{
        className: "bdo-grey-button-modal",
      }}
    >
      <div className="my-4" />
      <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
        <Form.Item
          name="pool"
          label="Pool:"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: "Select a pool" }]}
        >
          <AutoComplete
            placeholder="Select a pool"
            allowClear
            options={getLabelsAsValues(pools)}
            onChange={(value: string) => {
              setState((prevState) => ({
                ...prevState,
                poolRegex: createRegex(value),
              }));
            }}
            filterOption={(inputValue, option) => {
              return poolRegex.test(option!.value);
            }}
          ></AutoComplete>
        </Form.Item>

        <Form.Item
          name="telephoneNumbers"
          label="Numbers (Comma Separated):"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
            {
              validator: (_, value) =>
                NumericValidator({
                  value: value,
                  form: form,
                  fieldName: "telephoneNumbers",
                }),
            },
            {
              validator: (_, value) => LengthValidator({ value: value }),
            },
          ]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Input.TextArea
            className="h-48"
            placeholder="Enter telephone numbers, comma separated"
            allowClear
          ></Input.TextArea>
        </Form.Item>
      </Form>

      {submitError && <div className="text-red-500">{state.submitError}</div>}
    </Modal>
  );
};

export default AddNewNumbersFormModal;
